import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys, RECENT_DROPS_LIMIT } from '../lib/queryClient';
import { useAuthedQuery } from './useAuthedQuery';

export function useComebackBonus(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.comebackBonus,
    queryFn: async () => {
      const response = await apiClient.api.homeTasksControllerGetComebackBonuses();
      return response.bonuses.find((b) => b.canClaim) || null;
    },
    enabled,
  });
}

export function useChannels(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.channels,
    queryFn: async () => {
      const response = await apiClient.api.homeTasksControllerGetSubscriptionChannels();
      return response.channels;
    },
    enabled,
  });
}

export function useBoostChannels(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.boostChannels,
    queryFn: async () => {
      const response = await apiClient.api.homeTasksControllerGetBoostChannels();
      return response.channels;
    },
    enabled,
  });
}

export function useCheckSubscriptions() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.api.homeTasksControllerCheckSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels });
    },
  });
}

export function useReferralBonusProgress(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.referralProgress,
    queryFn: async () => {
      const response = await apiClient.api.homeTasksControllerGetReferralBonusProgress();
      return response.progressToBonus;
    },
    enabled,
  });
}

export function useProfileTasks(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.profileTasks,
    queryFn: () => apiClient.api.profileTaskControllerGetStatus(),
    enabled,
  });
}

export function useActivateUsernameTask() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.api.profileTaskControllerActivateUsernameTask(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profileTasks });
    },
  });
}

export function useActivateBioTask() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.api.profileTaskControllerActivateBioTask(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profileTasks });
    },
  });
}

export function useDailyBonus(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.dailyBonus,
    queryFn: async () => {
      const status = await apiClient.api.homeTasksControllerGetDailyBonusStatus();
      return {
        ...status,
        timerEndTime: status.canClaim ? null : Date.now() + status.timeUntilNextBonus,
      };
    },
    enabled,
  });
}

export function useTopRecentDrops(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.topDrops,
    queryFn: async () => {
      const response = await apiClient.api.inventoryControllerGetTopRecentDrops({ limit: '40' });
      return response.items;
    },
    enabled,
  });
}

export function useRecentDrops(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['home', 'recentDrops'] as const,
    queryFn: async () => {
      const response = await apiClient.api.inventoryControllerGetRecentInventoryItems({ limit: String(RECENT_DROPS_LIMIT) });
      return response.items;
    },
    enabled,
  });
}

export function useActiveDraws(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.draws,
    queryFn: () => apiClient.api.drawsControllerGetActiveDraws(),
    enabled,
  });
}

export function useActiveBuff(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.activeBuff,
    queryFn: async () => {
      try {
        return await apiClient.api.promocodeControllerGetActivePromocodeBuff();
      } catch {
        return null;
      }
    },
    enabled,
  });
}

export function useGlobalBuff(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.globalBuff,
    queryFn: async () => {
      try {
        return await apiClient.api.promocodeControllerGetGlobalPromocodeBuff();
      } catch {
        return null;
      }
    },
    enabled,
  });
}

export function useHomeDailyGiveaways(enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.dailyGiveaways,
    queryFn: async () => {
      const response = await apiClient.api.dailyGiveawayControllerGetDailyGiveaways();
      return response.giveaways;
    },
    enabled,
    staleTime: 10 * 1000,
  });
}

export function useHomeSnapshot(enabled = true) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: queryKeys.homeSnapshot,
    queryFn: () => apiClient.api.homeControllerGetHome(),
    enabled,
  });
}
